import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import Button from './Button';
import { useUpdateTicket, type RecordingTicket, type UpdateTicketPayload } from '../hooks/useTickets';
import { normalizeStatus } from '../utils/roles';

interface EditTicketModalProps {
  open: boolean;
  ticket: RecordingTicket | null;
  onClose: () => void;
}

function toDatetimeLocalValue(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocalValue(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const d = new Date(trimmed);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

const EditTicketModal: React.FC<EditTicketModalProps> = ({ open, ticket, onClose }) => {
  const updateMutation = useUpdateTicket();
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [description, setDescription] = useState('');
  const [sessionDateTime, setSessionDateTime] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [preferredLanguage, setPreferredLanguage] = useState('en');
  const [bookingId, setBookingId] = useState('');

  useEffect(() => {
    if (!ticket) return;
    setTitle(ticket.title || '');
    setNotes(ticket.notes || '');
    setDescription(ticket.description || '');
    setSessionDateTime(toDatetimeLocalValue(ticket.slotStartAt));
    setCustomerName(ticket.customerName || '');
    setCustomerPhone(ticket.customerPhone || '');
    setPreferredLanguage(ticket.preferredLanguage || 'en');
    setBookingId(ticket.bookingId != null ? String(ticket.bookingId) : '');
  }, [ticket]);

  if (!open || !ticket) return null;

  const status = normalizeStatus(ticket.status);
  const blocked = status === 'recording' || status === 'processing' || status === 'sent';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    const payload: UpdateTicketPayload = {
      title: title.trim(),
      notes,
      description: description || undefined,
      customerName: customerName || undefined,
      customerPhone: customerPhone || undefined,
      preferredLanguage,
      slotStartAt: fromDatetimeLocalValue(sessionDateTime),
    };

    const bid = bookingId.trim();
    if (bid) payload.bookingId = parseInt(bid, 10);

    await updateMutation.mutateAsync({ id: ticket.id, data: payload });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Edit ticket</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {blocked ? (
          <div className="p-6 text-sm text-amber-800 bg-amber-50">
            This ticket cannot be edited while status is {status}. Cancel or wait until processing finishes.
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input className="etr-input w-full" value={title} onChange={(e) => setTitle(e.target.value)} required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Session date &amp; time</label>
              <input
                type="datetime-local"
                className="etr-input w-full"
                value={sessionDateTime}
                onChange={(e) => setSessionDateTime(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Booking ID (optional)</label>
              <input className="etr-input w-full" value={bookingId} onChange={(e) => setBookingId(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Customer name</label>
              <input className="etr-input w-full" value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Customer phone</label>
              <input className="etr-input w-full" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Preferred language</label>
              <select className="etr-input w-full" value={preferredLanguage} onChange={(e) => setPreferredLanguage(e.target.value)}>
                <option value="en">English</option>
                <option value="ar">Arabic</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea className="etr-input w-full" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                className="etr-input w-full"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" variant="primary" className="flex-1" isLoading={updateMutation.isLoading}>
                Save changes
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default EditTicketModal;
